import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/20/solid";
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ClockIcon,
    FireIcon,
    ShieldExclamationIcon,
    PencilSquareIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    XMarkIcon,
    InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "src/components/base/dialog";
import { Input } from "src/components/base/input";
import { Select } from "src/components/base/select";
import { Field, Label } from "src/components/base/fieldset";
import { Switch } from "src/components/base/switch";
import { Checkbox } from "src/components/base/checkbox";
import { MOCK_SOC_EXCEPTIONS } from "../data/settings";
import type { SocException } from "../data/settings";
import { Card, StatCard } from "../shared";

// ---------------------------------------------------------------------------
// Extended types
// ---------------------------------------------------------------------------

interface ExtendedSocException extends SocException {
    hitCount: number;
    category: string;
    createdBy: string;
    createdAt: string;
    modifiedBy: string | null;
    modifiedAt: string | null;
}

interface AuditEntry {
    timestamp: string;
    user: string;
    action: string;
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const typeColor = { ip: "blue", domain: "purple", hash: "orange", user: "green", rule: "zinc" } as const;

const EXTENDED_EXCEPTIONS: ExtendedSocException[] = [
    {
        ...MOCK_SOC_EXCEPTIONS[0],
        hitCount: 1247,
        category: "Infrastructure",
        createdBy: "m.mustermann",
        createdAt: "2025-08-15",
        modifiedBy: "s.braun",
        modifiedAt: "2026-01-10",
    },
    {
        ...MOCK_SOC_EXCEPTIONS[1],
        hitCount: 89,
        category: "Vendor",
        createdBy: "a.weber",
        createdAt: "2025-09-01",
        modifiedBy: null,
        modifiedAt: null,
    },
    {
        ...MOCK_SOC_EXCEPTIONS[2],
        hitCount: 342,
        category: "Testing",
        createdBy: "j.mueller",
        createdAt: "2025-10-20",
        modifiedBy: "m.mustermann",
        modifiedAt: "2026-02-14",
    },
    {
        ...MOCK_SOC_EXCEPTIONS[3],
        hitCount: 15823,
        category: "Infrastructure",
        createdBy: "m.mustermann",
        createdAt: "2025-07-01",
        modifiedBy: "a.weber",
        modifiedAt: "2026-03-01",
    },
    {
        ...MOCK_SOC_EXCEPTIONS[4],
        hitCount: 56,
        category: "Infrastructure",
        createdBy: "s.braun",
        createdAt: "2025-11-15",
        modifiedBy: null,
        modifiedAt: null,
    },
    {
        ...MOCK_SOC_EXCEPTIONS[5],
        hitCount: 4891,
        category: "Development",
        createdBy: "j.mueller",
        createdAt: "2026-01-05",
        modifiedBy: "j.mueller",
        modifiedAt: "2026-03-10",
    },
];

const MOCK_AUDIT_TRAIL: Record<string, AuditEntry[]> = {
    "exc-1": [
        { timestamp: "2025-08-15 09:30", user: "m.mustermann", action: "Created exception" },
        { timestamp: "2025-12-01 14:22", user: "s.braun", action: "Updated value from 10.0.50.5 to 10.0.50.10" },
        { timestamp: "2026-01-10 11:05", user: "s.braun", action: "Extended expiry to 2026-06-30" },
    ],
    "exc-2": [{ timestamp: "2025-09-01 10:00", user: "a.weber", action: "Created exception" }],
    "exc-3": [
        { timestamp: "2025-10-20 15:45", user: "j.mueller", action: "Created exception" },
        { timestamp: "2026-02-14 08:30", user: "m.mustermann", action: "Verified hash still valid" },
    ],
    "exc-4": [
        { timestamp: "2025-07-01 08:00", user: "m.mustermann", action: "Created exception" },
        { timestamp: "2025-11-15 16:20", user: "a.weber", action: "Reviewed and approved" },
        { timestamp: "2026-03-01 09:15", user: "a.weber", action: "Extended expiry to 2026-12-31" },
    ],
    "exc-5": [
        { timestamp: "2025-11-15 14:00", user: "s.braun", action: "Created exception" },
        { timestamp: "2026-02-28 10:30", user: "s.braun", action: "Deactivated - reviewing necessity" },
    ],
    "exc-6": [
        { timestamp: "2026-01-05 11:30", user: "j.mueller", action: "Created exception" },
        { timestamp: "2026-03-10 13:45", user: "j.mueller", action: "Updated CIDR range" },
    ],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getExpiryStatus(expiry: string): "expired" | "warning" | "ok" | "none" {
    if (expiry === "—") return "none";
    const expiryDate = new Date(expiry);
    const now = new Date("2026-03-13");
    const diffDays = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "expired";
    if (diffDays <= 30) return "warning";
    return "ok";
}

function daysUntilExpiry(expiry: string): number {
    if (expiry === "—") return Infinity;
    const expiryDate = new Date(expiry);
    const now = new Date("2026-03-13");
    return Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function SectionHeading({
    children,
    icon: Icon,
    description,
}: {
    children: React.ReactNode;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    description?: string;
}) {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-blue-500" />
                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">{children}</h3>
            </div>
            {description && <p className="ml-7 mt-1 text-xs text-zinc-500 dark:text-zinc-400">{description}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SettingsSocExceptions() {
    const { t } = useTranslation();
    const [exceptions, setExceptions] = useState<ExtendedSocException[]>(EXTENDED_EXCEPTIONS);
    const [showAdd, setShowAdd] = useState(false);
    const [showAudit, setShowAudit] = useState<string | null>(null);

    // Selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showBulkConfirm, setShowBulkConfirm] = useState<"activate" | "deactivate" | "delete" | null>(null);

    // Search & filter
    const [searchQuery, setSearchQuery] = useState("");
    const [filterCategory, setFilterCategory] = useState<string>("all");
    const [filterType, setFilterType] = useState<string>("all");
    const [filterStatus, setFilterStatus] = useState<string>("all");

    // Collapsible categories
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

    // New exception form
    const [newName, setNewName] = useState("");
    const [newType, setNewType] = useState("ip");
    const [newValue, setNewValue] = useState("");
    const [newReason, setNewReason] = useState("");
    const [newExpiry, setNewExpiry] = useState("");
    const [newCategory, setNewCategory] = useState("Infrastructure");
    const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

    // Import/export
    const [showImport, setShowImport] = useState(false);

    // --- Derived data ---

    const categories = useMemo(() => {
        const cats = new Set(exceptions.map((e) => e.category));
        return Array.from(cats).sort();
    }, [exceptions]);

    const filteredExceptions = useMemo(() => {
        return exceptions.filter((exc) => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                if (
                    !exc.name.toLowerCase().includes(q) &&
                    !exc.value.toLowerCase().includes(q) &&
                    !exc.reason.toLowerCase().includes(q)
                ) {
                    return false;
                }
            }
            if (filterCategory !== "all" && exc.category !== filterCategory) return false;
            if (filterType !== "all" && exc.type !== filterType) return false;
            if (filterStatus === "active" && !exc.active) return false;
            if (filterStatus === "inactive" && exc.active) return false;
            if (filterStatus === "expired" && getExpiryStatus(exc.expiry) !== "expired") return false;
            if (filterStatus === "expiring" && getExpiryStatus(exc.expiry) !== "warning") return false;
            return true;
        });
    }, [exceptions, searchQuery, filterCategory, filterType, filterStatus]);

    const groupedExceptions = useMemo(() => {
        const groups: Record<string, ExtendedSocException[]> = {};
        for (const exc of filteredExceptions) {
            if (!groups[exc.category]) groups[exc.category] = [];
            groups[exc.category].push(exc);
        }
        return groups;
    }, [filteredExceptions]);

    const stats = useMemo(() => {
        const total = exceptions.length;
        const active = exceptions.filter((e) => e.active).length;
        const expired = exceptions.filter((e) => getExpiryStatus(e.expiry) === "expired").length;
        const expiring = exceptions.filter((e) => getExpiryStatus(e.expiry) === "warning").length;
        const mostTriggered = [...exceptions].sort((a, b) => b.hitCount - a.hitCount)[0];
        const totalHits = exceptions.reduce((sum, e) => sum + e.hitCount, 0);
        return { total, active, inactive: total - active, expired, expiring, mostTriggered, totalHits };
    }, [exceptions]);

    // --- Callbacks ---

    const toggleActive = useCallback((id: string) => {
        setExceptions((prev) => prev.map((exc) => (exc.id === id ? { ...exc, active: !exc.active } : exc)));
    }, []);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selectedIds.size === filteredExceptions.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredExceptions.map((e) => e.id)));
        }
    }, [selectedIds.size, filteredExceptions]);

    const executeBulkAction = useCallback(
        (action: "activate" | "deactivate" | "delete") => {
            if (action === "delete") {
                setExceptions((prev) => prev.filter((e) => !selectedIds.has(e.id)));
            } else {
                setExceptions((prev) =>
                    prev.map((e) => (selectedIds.has(e.id) ? { ...e, active: action === "activate" } : e)),
                );
            }
            setSelectedIds(new Set());
            setShowBulkConfirm(null);
        },
        [selectedIds],
    );

    const toggleCategory = useCallback((category: string) => {
        setCollapsedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    }, []);

    const checkDuplicate = useCallback(
        (value: string, type: string) => {
            const match = exceptions.find((e) => e.value === value && e.type === type);
            if (match) {
                setDuplicateWarning(`Similar exception already exists: "${match.name}" (${match.value})`);
            } else {
                // Check partial matches
                const partial = exceptions.find(
                    (e) => e.type === type && (e.value.includes(value) || value.includes(e.value)),
                );
                if (partial) {
                    setDuplicateWarning(`Possible overlap with: "${partial.name}" (${partial.value})`);
                } else {
                    setDuplicateWarning(null);
                }
            }
        },
        [exceptions],
    );

    const handleNewValueChange = useCallback(
        (val: string) => {
            setNewValue(val);
            if (val.length > 2) checkDuplicate(val, newType);
            else setDuplicateWarning(null);
        },
        [checkDuplicate, newType],
    );

    const addException = useCallback(() => {
        const newExc: ExtendedSocException = {
            id: `exc-${Date.now()}`,
            name: newName,
            type: newType as ExtendedSocException["type"],
            value: newValue,
            reason: newReason,
            expiry: newExpiry || "—",
            active: true,
            hitCount: 0,
            category: newCategory,
            createdBy: "m.mustermann",
            createdAt: "2026-03-13",
            modifiedBy: null,
            modifiedAt: null,
        };
        setExceptions((prev) => [...prev, newExc]);
        setShowAdd(false);
        setNewName("");
        setNewValue("");
        setNewReason("");
        setNewExpiry("");
        setDuplicateWarning(null);
    }, [newName, newType, newValue, newReason, newExpiry, newCategory]);

    const mockExportCsv = useCallback(() => {
        const header = "id,name,type,value,reason,expiry,active,hitCount,category";
        const rows = exceptions.map(
            (e) =>
                `${e.id},${e.name},${e.type},${e.value},${e.reason},${e.expiry},${e.active},${e.hitCount},${e.category}`,
        );
        const csv = [header, ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "soc_exceptions_export.csv";
        a.click();
        URL.revokeObjectURL(url);
    }, [exceptions]);

    return (
        <>
            {/* Header */}
            <div className="flex items-center justify-between">
                <Heading>{t("settings.socExceptions.title")}</Heading>
                <div className="flex gap-2">
                    <Button plain onClick={() => setShowImport(true)}>
                        <ArrowUpTrayIcon className="h-4 w-4" />
                        Import
                    </Button>
                    <Button plain onClick={mockExportCsv}>
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Export CSV
                    </Button>
                    <Button color="blue" onClick={() => setShowAdd(true)}>
                        <PlusIcon className="h-4 w-4" />
                        {t("settings.socExceptions.add")}
                    </Button>
                </div>
            </div>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">{t("settings.socExceptions.description")}</p>

            {/* ============================================================ */}
            {/* STATS                                                        */}
            {/* ============================================================ */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Total Exceptions" value={String(stats.total)} />
                <StatCard
                    label="Active / Inactive"
                    value={`${stats.active} / ${stats.inactive}`}
                    trend={`${Math.round((stats.active / stats.total) * 100)}% active`}
                />
                <StatCard label="Total Hits" value={stats.totalHits.toLocaleString()} trend="Across all exceptions" />
                <StatCard
                    label="Most Triggered"
                    value={stats.mostTriggered ? stats.mostTriggered.name : "—"}
                    trend={stats.mostTriggered ? `${stats.mostTriggered.hitCount.toLocaleString()} hits` : ""}
                />
            </div>

            {/* Expiry warnings */}
            {(stats.expired > 0 || stats.expiring > 0) && (
                <div className="mt-4 flex flex-wrap gap-3">
                    {stats.expired > 0 && (
                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 dark:border-red-900/50 dark:bg-red-950/20">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                            <span className="text-xs font-medium text-red-700 dark:text-red-300">
                                {stats.expired} expired exception(s)
                            </span>
                        </div>
                    )}
                    {stats.expiring > 0 && (
                        <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50/50 px-3 py-2 dark:border-yellow-900/50 dark:bg-yellow-950/20">
                            <ClockIcon className="h-4 w-4 text-yellow-500" />
                            <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                                {stats.expiring} expiring within 30 days
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* ============================================================ */}
            {/* SEARCH & FILTER                                              */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search exceptions..."
                            className="w-full rounded-lg border border-zinc-200 bg-white py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                                <XMarkIcon className="h-4 w-4 text-zinc-400" />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <FunnelIcon className="h-4 w-4 text-zinc-400" />
                        <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                            <option value="all">All Categories</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </Select>
                        <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                            <option value="all">All Types</option>
                            <option value="ip">IP</option>
                            <option value="domain">Domain</option>
                            <option value="hash">Hash</option>
                            <option value="user">User</option>
                            <option value="rule">Rule</option>
                        </Select>
                        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="expired">Expired</option>
                            <option value="expiring">Expiring Soon</option>
                        </Select>
                    </div>
                </div>
            </Card>

            {/* ============================================================ */}
            {/* BULK ACTIONS                                                 */}
            {/* ============================================================ */}
            {selectedIds.size > 0 && (
                <div className="mt-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-800/50 dark:bg-blue-950/20">
                    <Badge color="blue">{selectedIds.size} selected</Badge>
                    <div className="flex gap-2">
                        <Button plain onClick={() => setShowBulkConfirm("activate")}>
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            Activate
                        </Button>
                        <Button plain onClick={() => setShowBulkConfirm("deactivate")}>
                            <XMarkIcon className="h-4 w-4 text-yellow-500" />
                            Deactivate
                        </Button>
                        <Button plain onClick={() => setShowBulkConfirm("delete")}>
                            <TrashIcon className="h-4 w-4 text-red-500" />
                            Delete
                        </Button>
                    </div>
                    <Button plain onClick={() => setSelectedIds(new Set())} className="ml-auto">
                        Clear Selection
                    </Button>
                </div>
            )}

            {/* ============================================================ */}
            {/* GROUPED EXCEPTIONS TABLE                                     */}
            {/* ============================================================ */}
            <div className="mt-6 space-y-4">
                {Object.entries(groupedExceptions).map(([category, excs]) => (
                    <Card key={category}>
                        {/* Category header */}
                        <button
                            className="flex w-full items-center gap-2 text-left"
                            onClick={() => toggleCategory(category)}
                        >
                            {collapsedCategories.has(category) ? (
                                <ChevronRightIcon className="h-4 w-4 text-zinc-400" />
                            ) : (
                                <ChevronDownIcon className="h-4 w-4 text-zinc-400" />
                            )}
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">{category}</h3>
                            <Badge color="zinc">{excs.length}</Badge>
                        </button>

                        {!collapsedCategories.has(category) && (
                            <Table className="mt-4" dense>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>
                                            <Checkbox
                                                checked={excs.every((e) => selectedIds.has(e.id))}
                                                onChange={toggleSelectAll}
                                            />
                                        </TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colName")}</TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colType")}</TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colValue")}</TableHeader>
                                        <TableHeader>Hits</TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colReason")}</TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colExpiry")}</TableHeader>
                                        <TableHeader>{t("settings.socExceptions.colActive")}</TableHeader>
                                        <TableHeader>Audit</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {excs.map((exc) => {
                                        const expiryStatus = getExpiryStatus(exc.expiry);
                                        const days = daysUntilExpiry(exc.expiry);
                                        return (
                                            <TableRow
                                                key={exc.id}
                                                className={
                                                    expiryStatus === "expired"
                                                        ? "bg-red-50/50 dark:bg-red-950/10"
                                                        : expiryStatus === "warning"
                                                          ? "bg-yellow-50/50 dark:bg-yellow-950/10"
                                                          : ""
                                                }
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIds.has(exc.id)}
                                                        onChange={() => toggleSelect(exc.id)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium">{exc.name}</TableCell>
                                                <TableCell>
                                                    <Badge color={typeColor[exc.type]}>{exc.type}</Badge>
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{exc.value}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <FireIcon
                                                            className={`h-3.5 w-3.5 ${exc.hitCount > 1000 ? "text-red-500" : exc.hitCount > 100 ? "text-orange-500" : "text-zinc-400"}`}
                                                        />
                                                        <span className="font-mono text-xs font-semibold">
                                                            {exc.hitCount.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="max-w-xs truncate text-sm text-zinc-500">
                                                    {exc.reason}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5">
                                                        <span
                                                            className={`font-mono text-xs ${expiryStatus === "expired" ? "font-semibold text-red-600 dark:text-red-400" : expiryStatus === "warning" ? "font-semibold text-yellow-600 dark:text-yellow-400" : "text-zinc-500"}`}
                                                        >
                                                            {exc.expiry}
                                                        </span>
                                                        {expiryStatus === "expired" && (
                                                            <Badge color="red">Expired</Badge>
                                                        )}
                                                        {expiryStatus === "warning" && (
                                                            <Badge color="yellow">{days}d left</Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={exc.active}
                                                        onChange={() => toggleActive(exc.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button plain onClick={() => setShowAudit(exc.id)}>
                                                        <PencilSquareIcon className="h-4 w-4 text-zinc-400" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                ))}
            </div>

            {filteredExceptions.length === 0 && (
                <Card className="mt-6">
                    <div className="flex flex-col items-center gap-2 py-8">
                        <ShieldExclamationIcon className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                        <p className="text-sm text-zinc-500">No exceptions match your filters</p>
                    </div>
                </Card>
            )}

            {/* ============================================================ */}
            {/* DIALOGS                                                      */}
            {/* ============================================================ */}

            {/* Add exception dialog */}
            <Dialog
                open={showAdd}
                onClose={() => {
                    setShowAdd(false);
                    setDuplicateWarning(null);
                }}
                size="lg"
            >
                <DialogTitle>{t("settings.socExceptions.addTitle")}</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <Field>
                            <Label>{t("settings.socExceptions.colName")}</Label>
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={t("settings.socExceptions.namePlaceholder")}
                            />
                        </Field>
                        <div className="grid grid-cols-3 gap-4">
                            <Field>
                                <Label>{t("settings.socExceptions.colType")}</Label>
                                <Select value={newType} onChange={(e) => setNewType(e.target.value)}>
                                    <option value="ip">IP</option>
                                    <option value="domain">Domain</option>
                                    <option value="hash">Hash</option>
                                    <option value="user">User</option>
                                    <option value="rule">Rule</option>
                                </Select>
                            </Field>
                            <Field>
                                <Label>Category</Label>
                                <Select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                                    <option value="Infrastructure">Infrastructure</option>
                                    <option value="Vendor">Vendor</option>
                                    <option value="Testing">Testing</option>
                                    <option value="Development">Development</option>
                                    <option value="Other">Other</option>
                                </Select>
                            </Field>
                            <Field>
                                <Label>{t("settings.socExceptions.colExpiry")}</Label>
                                <Input type="date" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} />
                            </Field>
                        </div>
                        <Field>
                            <Label>{t("settings.socExceptions.colValue")}</Label>
                            <Input
                                value={newValue}
                                onChange={(e) => handleNewValueChange(e.target.value)}
                                placeholder={t("settings.socExceptions.valuePlaceholder")}
                            />
                            {duplicateWarning && (
                                <div className="mt-2 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2 dark:border-yellow-800/50 dark:bg-yellow-950/20">
                                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-yellow-500" />
                                    <span className="text-xs text-yellow-700 dark:text-yellow-300">
                                        {duplicateWarning}
                                    </span>
                                </div>
                            )}
                        </Field>
                        <Field>
                            <Label>{t("settings.socExceptions.colReason")}</Label>
                            <Input
                                value={newReason}
                                onChange={(e) => setNewReason(e.target.value)}
                                placeholder={t("settings.socExceptions.reasonPlaceholder")}
                            />
                        </Field>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button
                        plain
                        onClick={() => {
                            setShowAdd(false);
                            setDuplicateWarning(null);
                        }}
                    >
                        {t("tickets.cancel")}
                    </Button>
                    <Button color="blue" onClick={addException}>
                        {t("tickets.submit")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Audit trail dialog */}
            <Dialog open={showAudit !== null} onClose={() => setShowAudit(null)} size="lg">
                <DialogTitle>Audit Trail</DialogTitle>
                <DialogBody>
                    {showAudit && (
                        <div className="space-y-4">
                            {/* Exception info */}
                            {(() => {
                                const exc = exceptions.find((e) => e.id === showAudit);
                                if (!exc) return null;
                                return (
                                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800">
                                        <div className="flex items-center gap-2">
                                            <Badge color={typeColor[exc.type]}>{exc.type}</Badge>
                                            <span className="font-medium">{exc.name}</span>
                                            <span className="font-mono text-xs text-zinc-400">{exc.value}</span>
                                        </div>
                                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>
                                                Created by:{" "}
                                                <span className="font-medium text-zinc-700 dark:text-zinc-300">
                                                    {exc.createdBy}
                                                </span>{" "}
                                                on {exc.createdAt}
                                            </div>
                                            {exc.modifiedBy && (
                                                <div>
                                                    Last modified by:{" "}
                                                    <span className="font-medium text-zinc-700 dark:text-zinc-300">
                                                        {exc.modifiedBy}
                                                    </span>{" "}
                                                    on {exc.modifiedAt}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Timeline */}
                            <div className="space-y-0">
                                {(MOCK_AUDIT_TRAIL[showAudit] ?? []).map((entry, i) => (
                                    <div
                                        key={i}
                                        className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                                    >
                                        <div className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" />
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs text-zinc-400">{entry.timestamp}</span>
                                            <Badge color="zinc">{entry.user}</Badge>
                                        </div>
                                        <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{entry.action}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowAudit(null)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk action confirm dialog */}
            <Dialog open={showBulkConfirm !== null} onClose={() => setShowBulkConfirm(null)} size="sm">
                <DialogTitle>Confirm Bulk Action</DialogTitle>
                <DialogBody>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        Are you sure you want to <span className="font-semibold">{showBulkConfirm}</span>{" "}
                        {selectedIds.size} selected exception(s)?
                    </p>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowBulkConfirm(null)}>
                        Cancel
                    </Button>
                    <Button
                        color={showBulkConfirm === "delete" ? "red" : "blue"}
                        onClick={() => showBulkConfirm && executeBulkAction(showBulkConfirm)}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Import dialog */}
            <Dialog open={showImport} onClose={() => setShowImport(false)} size="lg">
                <DialogTitle>Import Exceptions</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <div className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 p-8 dark:border-zinc-600 dark:bg-zinc-800">
                            <ArrowUpTrayIcon className="h-8 w-8 text-zinc-400" />
                            <p className="text-sm text-zinc-500">Drag and drop a CSV file, or click to browse</p>
                            <Button plain>Browse Files</Button>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800/50 dark:bg-blue-950/20">
                            <InformationCircleIcon className="h-4 w-4 shrink-0 text-blue-500" />
                            <span className="text-xs text-blue-700 dark:text-blue-300">
                                Expected format: id, name, type, value, reason, expiry, active, category (CSV)
                            </span>
                        </div>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowImport(false)}>
                        Cancel
                    </Button>
                    <Button color="blue" onClick={() => setShowImport(false)}>
                        Import
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
